import { useState } from 'react'
import { useAppModule } from '../appContext.ts'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { urls } from '../../shared/urls.ts'
import { Icon, IconName } from '../../shared/ui/icon/icon.tsx'
import { cn } from '../../shared/lib/utils.ts'
import { useSnapshot } from 'valtio/react'
import { FormattedMessage } from 'react-intl'
import { LogoRive } from '../../shared/ui/logo/Logo.tsx'
import { AppMessageKeys } from '../../shared/translations/messages.ts'

const menuItems: {
  text: AppMessageKeys
  id: string
  icon: IconName
  url: string
}[] = [
  {
    text: 'navbar.main',
    id: 'navbar-main',
    icon: 'home',
    url: urls.main,
  },
  {
    text: 'navbar.freeTalk',
    id: 'navbar-free-talk',
    icon: 'mic',
    url: urls.freeTalk,
  },
  {
    text: 'navbar.lessons',
    id: 'navbar-lessons',
    icon: 'navStar',
    url: urls.learningProfile(),
  },
  {
    text: 'navbar.vocabPlus',
    id: 'navbar-vocabPlus',
    icon: 'book',
    url: urls.practice,
  },
  {
    text: 'navbar.progress',
    id: 'navbar-progress',
    icon: 'trendUp',
    url: urls.progress,
  },
  {
    text: 'navbar.notifications',
    id: 'navbar-notifications',
    icon: 'bell',
    url: urls.notifications,
  },
  {
    text: 'navbar.profile',
    id: 'navbar-profile',
    icon: 'profile2',
    url: urls.profile(),
  },
]

const navLinkClassName = (params: { isActive: boolean }) =>
  cn(
    'flex h-44 items-center gap-12 rounded-12 px-14 text-14 font-semibold transition-colors 2xl:h-60 2xl:gap-16 2xl:rounded-18 2xl:px-18 2xl:text-18',
    params.isActive
      ? 'bg-white font-bold text-main shadow-button'
      : 'text-gray-dark hover:bg-white/60 hover:text-black',
  )

export const Menu = (props: { className?: string }) => {
  const appModule = useAppModule()
  const authStore = appModule.authStore
  const authState = useSnapshot(authStore.state)
  const isAnonOrLogout = authState.isAnon || !authState.isLogin
  const [showNotificationsHint, setShowNotificationsHint] = useState(
    () => appModule.appStorage.get('showNotificationsHint') !== false,
  )

  return (
    <aside
      aria-label="main menu"
      className={cn(
        'sticky top-0 flex h-dvh w-[220px] shrink-0 cursor-default flex-col overflow-y-auto pt-48 text-14 2xl:w-[300px] 2xl:pt-64 2xl:text-18',
        props.className,
      )}
    >
      <div className="flex min-h-full flex-col gap-28 px-6 pb-40 2xl:gap-40 2xl:px-8 2xl:pb-56">
        <Link to={urls.main} className="px-6 2xl:px-8">
          <LogoRive />
        </Link>

        <nav>
          <ul className="flex list-none flex-col gap-4 p-0 2xl:gap-8">
            {menuItems.map((item) => {
              const isNotifications = item.text === 'navbar.notifications'
              return (
                <li key={item.text}>
                  <NavLink
                    to={item.url}
                    id={item.id}
                    className={navLinkClassName}
                    onClick={() => {
                      if (!isNotifications || !showNotificationsHint) {
                        return
                      }
                      appModule.appStorage.set('showNotificationsHint', false)
                      setShowNotificationsHint(false)
                    }}
                  >
                    <Icon className="shrink-0" size="sm" iconName={item.icon} />
                    <FormattedMessage id={item.text} />
                    {isNotifications && showNotificationsHint ? (
                      <span
                        aria-hidden
                        className="ml-auto size-8 shrink-0 animate-notificationsHint rounded-full 2xl:size-12"
                      />
                    ) : null}
                  </NavLink>
                </li>
              )
            })}
          </ul>
        </nav>

        {!isAnonOrLogout ? (
          <>
            <div role="separator" className="mx-14 h-1 shrink-0 bg-gray" />
            <ul className="mt-auto flex list-none flex-col gap-4 p-0">
              <li>
                <NavLink className={navLinkClassName} to={urls.contacts}>
                  <Icon iconName="help" size="sm" />
                  <FormattedMessage id="Help" />
                </NavLink>
              </li>
              <li>
                <LoginLogout />
              </li>
            </ul>
          </>
        ) : null}
      </div>
    </aside>
  )
}

function LoginLogout() {
  const authStore = useAppModule().authStore
  const navigate = useNavigate()
  const state = useSnapshot(authStore.state)
  const logout = state.isLogin && !state.isAnon

  return (
    <button
      data-testid="logout-btn"
      type="button"
      className={navLinkClassName({ isActive: false })}
      onClick={() => {
        if (logout) {
          authStore.logoutAndRefresh()
        } else {
          navigate(urls.login)
        }
      }}
    >
      <Icon iconName="exit" size="sm" />
      <FormattedMessage id={logout ? 'Log out' : 'Log in'} />
    </button>
  )
}
